import "./VariableTicketContainer.css";
import { useEffect, useMemo, useState } from "react";
import { Input, parseInlineStyle } from "@iq/models";

function normalizeChild1(child1) {
    if (!child1) {
        return { title: "", fields: [], pill: "" };
    }

    const title = child1?.title || "";
    const subtitle = child1?.subtitle || "";
    const fields = Array.isArray(child1?.fields) ? child1.fields : [];
    const pill = child1?.pill || "";
    const stats = Array.isArray(child1?.stats) ? child1.stats : [];

    return {
        title,
        fields,
        pill,
        subtitle,
        stats
    };
}

function normalizeDropdownOptions(children) {
    if (!children) {
        return [];
    }

    if (children instanceof Map) {
        return Array.from(children.entries()).map(([id, label]) => ({
            value: String(id),
            label: String(label),
        }));
    }

    if (!Array.isArray(children)) {
        return [];
    }

    return children
        .map((item) => {
            if (item == null) {
                return null;
            }

            if (typeof item === "string" || typeof item === "number") {
                return {
                    value: String(item),
                    label: String(item),
                };
            }

            const value = item?.value ?? item?.id ?? item?.key;
            const label = item?.label ?? item?.name ?? item?.title ?? value;

            if (value == null || label == null) {
                return null;
            }

            return {
                value: String(value),
                label: String(label),
            };
        })
        .filter(Boolean);
}

function normalizeChild2(child2) {
    if (!child2 || !Array.isArray(child2?.inputs)) {
        return { inputs: [] };
    }

    return {
        inputs: child2.inputs.map((input, index) => {
            const type = String(input?.type || "text").toLowerCase();
            const normalizedType = ["text", "textarea", "dropdown", "date", "datetime-local"].includes(type)
                ? type
                : "text";

            return {
                key: input?.key || `input_${index + 1}`,
                label: input?.label || "",
                type: normalizedType,
                value: input?.value ?? "",
                style: input?.style ?? "",
                placeholder: input?.placeholder ?? "",
                disabled: Boolean(input?.disabled),
                rows: Number(input?.rows) > 0 ? Number(input.rows) : 4,
                options: normalizeDropdownOptions(input?.children),
                onChange: typeof input?.onChange === "function" ? input.onChange : null,
            };
        }),
    };
}

function renderChild2Input(input, value, handleInputChange) {
    switch (input.type) {
        case "textarea":
            return (
                <Input
                    type="textarea"
                    value={String(value || "")}
                    placeholder={input.placeholder}
                    rows={input.rows}
                    disabled={input.disabled}
                    readOnly={input.disabled}
                    onChange={(event) => handleInputChange(input, event.target.value)}
                    colorScheme="light"
                />
            );
        case "dropdown":
            {
                const selected = input.options.find((option) => String(option.value) === String(value));
                const displayValue = selected?.label || "";

                return (
                    <Input
                        type="dropdown"
                        value={displayValue}
                        placeholder={input.placeholder}
                        allowCustom={false}
                        colorScheme="light"
                        disabled={input.disabled}
                        options={input.options}
                        onOptionSelect={(option) => handleInputChange(input, option.value)}
                        className="it-variable-page-dropdown"
                        menuClassName="it-variable-page-dropdown-menu"
                        renderInPortal
                    />
                );
            }
        case "text":
        case "date":
        case "datetime-local":
        default:
            return (
                <Input
                    type={input.type}
                    value={String(value || "")}
                    placeholder={input.placeholder}
                    disabled={input.disabled}
                    readOnly={input.disabled}
                    onChange={(event) => handleInputChange(input, event.target.value)}
                    colorScheme="light"
                />
            );
    }
}

export default function VariableTicketContainer({ title, child1, child2, child3, actionButtons = [], child1ActionButtons = [] }) {
    const child1Block = useMemo(() => normalizeChild1(child1), [child1]);
    const child2Block = useMemo(() => normalizeChild2(child2), [child2]);
    const [child2Values, setChild2Values] = useState({});
    const visibleChild1ActionButtons = Array.isArray(child1ActionButtons)
        ? child1ActionButtons.filter((button) => button && button.visible !== false)
        : [];
    const visibleActionButtons = Array.isArray(actionButtons)
        ? actionButtons.filter((button) => button && button.visible !== false)
        : [];

    useEffect(() => {
        const nextValues = {};
        child2Block.inputs.forEach((input) => {
            nextValues[input.key] = String(input.value ?? "");
        });
        setChild2Values(nextValues);
    }, [child2Block]);

    function handleChild2InputChange(input, nextValue) {
        const normalizedValue = String(nextValue ?? "");
        setChild2Values((prev) => ({
            ...prev,
            [input.key]: normalizedValue,
        }));
        input.onChange?.(normalizedValue, input);
    }

    return (
        <div className="it-variable-page-container">
            <div className="it-variable-page-details-container">
                <div className="it-variable-page-child1-container">
                    <div className="it-variable-page-child1-details-container">
                        <div className="it-variable-page-child1-main">
                            <div className="it-variable-page-child1-header">
                                {child1Block.title ? <h3 className="it-variable-page-child1-title">{child1Block.title}</h3> : null}
                                {child1Block.pill ? <span className="it-variable-page-child1-pill">{child1Block.pill}</span> : null}
                            </div>
                            <div className="it-variable-page-child1-subtitle">
                                {child1Block.subtitle ? <h4 className="it-variable-page-child1-subtitle">{child1Block.subtitle}</h4> : null}
                            </div>
                            {/* <div className="it-variable-page-header-meta-container">
                                {child1Block.fields.map((item, index) => {
                                    const label = item?.label || "";
                                    const value = item?.value ?? "-";

                                    return (
                                        <div className="it-variable-page-field" key={index}>
                                            <span className="it-variable-page-field-label">{label}</span>
                                            <span className="it-variable-page-field-value">{String(value)}</span>
                                        </div>
                                    );
                                })}
                            </div> */}
                        </div>
                        {visibleChild1ActionButtons.length > 0 ? (
                            <div className="it-variable-page-child1-actions">
                                {visibleChild1ActionButtons.map((button, index) => (
                                    <button
                                        type="button"
                                        key={button.key || button.label || index}
                                        className={[
                                            "it-variable-page-action-button",
                                            button.variant === "secondary" ? "it-variable-page-action-button--secondary" : "",
                                        ].filter(Boolean).join(" ")}
                                        onClick={button.onClick}
                                        disabled={Boolean(button.disabled)}
                                    >
                                        {button.label}
                                    </button>
                                ))}
                            </div>
                        ) : null}
                    </div>
                    <div className="it-variable-page-child1-stats-container">
                        {child1Block.fields.map((item, index) => (
                            <div className="it-variable-page-child1-stat" key={index}>
                                <span className="it-variable-page-child1-stat-label">{item.label}</span>
                                <span className="it-variable-page-child1-stat-value">{item.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="it-variable-page-details-row it-variable-page-child2-container">
                    <div className="it-variable-page-child2-scroll">
                        <h1 className="it-variable-page-child2-title">{title}</h1>
                        <div className="it-variable-page-child2-grid">
                            {child2Block.inputs.map((input) => (
                                <div
                                    className="it-variable-page-child2-field"
                                    key={input.key}
                                    style={parseInlineStyle(input.style)}
                                >
                                    {input.label ? <label className="it-variable-page-child2-label">{input.label}</label> : null}
                                    {renderChild2Input(
                                        input,
                                        child2Values[input.key] ?? String(input.value ?? ""),
                                        handleChild2InputChange
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                    {visibleActionButtons.length > 0 ? (
                        <div className="it-variable-page-actions-row">
                            {visibleActionButtons.map((button, index) => (
                                <button
                                    type="button"
                                    key={button.key || button.label || index}
                                    className={[
                                        "it-variable-page-action-button",
                                        button.variant === "secondary" ? "it-variable-page-action-button--secondary" : "",
                                    ].filter(Boolean).join(" ")}
                                    onClick={button.onClick}
                                    disabled={Boolean(button.disabled)}
                                >
                                    {button.label}
                                </button>
                            ))}
                        </div>
                    ) : null}
                </div>
            </div>
            <div className="it-variable-page-details-column">
                {child3}
            </div>
        </div>
    );
}